/* ----------------------------------------------------------------------------
 * SEO-C (W3a) data fetchers — Backlinks-lite (beta / vendor-fragile), deeper
 * Technical Site Audit, and Local Map-Rank tracking.
 *
 * Self-contained and OWNED by Agent SEO-C-FE (kept out of the shared
 * data/sources.ts, which is orchestrator-wired). Mirrors the W2a SEO-B fetchers
 * exactly: gated behind USE_MOCK_DATA, honest nulls via numOrNull / strOrNull,
 * and every one of these three reads is PAID (DataForSEO, 7-day cache) so it is
 * USER-TRIGGERED ONLY — never fired on mount. Callers branch on ApiError: 503
 * provider_unavailable (nothing fetched/charged), 402 budget_exceeded (refused
 * before any provider call), 429 rate_limited, 502 provider_error (nothing
 * stored). Shapes match the LOCKED W3a API contract, including the honest
 * beta:true + vendor_fragile:true flags on backlinks-lite.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type {
  BacklinksLiteResult,
  MapRankResult,
  TechAuditIssue,
  TechAuditResult,
} from '@/types/seoc';

/** Finite number or null — blank/absent/non-numeric all stay null. NEVER 0. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Non-empty string or null — never coerces to a fabricated value. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** String or '' — for fields that are always present but may be blank. */
function strOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/* --- 1. Backlinks-lite ------------------------------------------------------- */
// GET /seo/projects/{id}/backlinks-lite?limit=50 — PAID (Backlinks), 7d cache.
// BETA + VENDOR-FRAGILE: envelope carries beta:true + vendor_fragile:true.

interface BacklinkRowDto {
  source_domain?: string | null;
  source_url?: string | null;
  target_url?: string | null;
  anchor?: string | null;
  dofollow?: boolean | null;
  first_seen?: string | null;
  rank?: number | string | null;
}

const MOCK_BACKLINKS_LITE: Record<string, BacklinksLiteResult> = {
  seo1: {
    backlinks: [
      {
        source_domain: 'techcrunch.com',
        source_url: 'https://techcrunch.com/2026/03/community-tools-roundup/',
        target_url: 'https://grow2gether.com/',
        anchor: 'community growth platform',
        dofollow: true,
        first_seen: '2026-03-14',
        rank: 812,
      },
      {
        source_domain: 'producthunt.com',
        source_url: 'https://www.producthunt.com/posts/grow2gether',
        target_url: 'https://grow2gether.com/features',
        anchor: 'Grow2gether',
        dofollow: true,
        first_seen: '2026-02-02',
        rank: 640,
      },
      // null-honest row: provider returned no rank for this linking page, and a
      // naked link with no anchor text.
      {
        source_domain: 'smallblog.example',
        source_url: 'https://smallblog.example/post/42',
        target_url: 'https://grow2gether.com/',
        anchor: '',
        dofollow: false,
        first_seen: '2026-01-20',
        rank: null,
      },
    ],
    summary: { total_backlinks: 1284, referring_domains: 213 },
    beta: true,
    vendor_fragile: true,
    source: 'dataforseo.backlinks',
    fetched_at: '2026-07-16T06:30:00Z',
    cache_hit: false,
  },
  seo2: {
    backlinks: [
      {
        source_domain: 'psychologytoday.com',
        source_url: 'https://www.psychologytoday.com/directory/booksesh',
        target_url: 'https://booksesh.com/',
        anchor: 'booking for therapists',
        dofollow: true,
        first_seen: '2026-04-01',
        rank: 905,
      },
    ],
    // null-honest summary: referring_domains not reported by the provider.
    summary: { total_backlinks: 342, referring_domains: null },
    beta: true,
    vendor_fragile: true,
    source: 'dataforseo.backlinks',
    fetched_at: '2026-07-15T18:22:00Z',
    cache_hit: true,
  },
};

/** Backlinks-lite for the project's domain (PAID, 7d cache; BETA /
 *  vendor-fragile). User-triggered only. */
export async function fetchSeoBacklinksLite(
  projectId: string,
  limit = 50,
): Promise<BacklinksLiteResult> {
  if (USE_MOCK_DATA) {
    const mock = MOCK_BACKLINKS_LITE[projectId] ?? {
      backlinks: [],
      summary: { total_backlinks: null, referring_domains: null },
      beta: true,
      vendor_fragile: true,
      source: 'dataforseo.backlinks',
      fetched_at: null,
      cache_hit: false,
    };
    return { ...mock, backlinks: mock.backlinks.slice(0, limit) };
  }
  const res = await api.get<{
    backlinks?: BacklinkRowDto[];
    summary?: { total_backlinks?: number | string | null; referring_domains?: number | string | null };
    beta?: boolean;
    vendor_fragile?: boolean;
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/backlinks-lite`, { limit });
  return {
    backlinks: (res.backlinks ?? []).map((b) => ({
      source_domain: strOrNull(b.source_domain) ?? '—',
      source_url: strOrEmpty(b.source_url),
      target_url: strOrEmpty(b.target_url),
      anchor: strOrEmpty(b.anchor),
      dofollow: b.dofollow === true,
      first_seen: strOrEmpty(b.first_seen),
      rank: numOrNull(b.rank),
    })),
    summary: {
      total_backlinks: numOrNull(res.summary?.total_backlinks),
      referring_domains: numOrNull(res.summary?.referring_domains),
    },
    // Honest to the contract: these are ALWAYS true for this feature, but we
    // read them from the envelope and never silently downgrade them to false.
    beta: res.beta !== false,
    vendor_fragile: res.vendor_fragile !== false,
    source: res.source ?? 'dataforseo.backlinks',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

/* --- 2. Technical Site Audit ------------------------------------------------- */
// GET /seo/projects/{id}/tech-audit?limit=100 — PAID (OnPage), 7d cache.

interface TechAuditIssueDto {
  type?: string | null;
  severity?: string | null;
  detail?: string | null;
}

interface TechAuditPageDto {
  url?: string | null;
  status_code?: number | string | null;
  issues?: TechAuditIssueDto[];
  load_time_ms?: number | string | null;
}

function mapIssues(issues: TechAuditIssueDto[] | undefined): TechAuditIssue[] {
  return (issues ?? []).map((i) => ({
    type: strOrNull(i.type) ?? 'unknown',
    severity: strOrNull(i.severity) ?? 'notice',
    detail: strOrEmpty(i.detail),
  }));
}

const MOCK_TECH_AUDIT: Record<string, TechAuditResult> = {
  seo1: {
    pages: [
      {
        url: 'https://grow2gether.com/',
        status_code: 200,
        issues: [{ type: 'missing_meta_description', severity: 'warning', detail: 'Home page has no meta description.' }],
        load_time_ms: 640,
      },
      {
        url: 'https://grow2gether.com/pricing',
        status_code: 200,
        issues: [
          { type: 'large_image', severity: 'notice', detail: 'Hero image is 1.8 MB — consider compressing.' },
          { type: 'missing_h1', severity: 'error', detail: 'No H1 element found on the page.' },
        ],
        load_time_ms: 1220,
      },
      // null-honest row: crawler could not reach the page, so no status /
      // timing were reported — rendered as em-dashes, never as 0.
      {
        url: 'https://grow2gether.com/legacy',
        status_code: null,
        issues: [{ type: 'broken_link', severity: 'error', detail: 'Linked from the footer but did not resolve.' }],
        load_time_ms: null,
      },
    ],
    summary: { pages_crawled: 48, errors: 3, warnings: 11 },
    source: 'dataforseo.onpage',
    fetched_at: '2026-07-16T06:34:00Z',
    cache_hit: false,
  },
  seo2: {
    pages: [
      {
        url: 'https://booksesh.com/',
        status_code: 200,
        issues: [],
        load_time_ms: 410,
      },
    ],
    // null-honest summary: warnings not reported by the provider.
    summary: { pages_crawled: 12, errors: 0, warnings: null },
    source: 'dataforseo.onpage',
    fetched_at: '2026-07-15T18:26:00Z',
    cache_hit: true,
  },
};

/** Deeper technical site audit for the project (PAID, 7d cache).
 *  User-triggered only. */
export async function fetchSeoTechAudit(
  projectId: string,
  limit = 100,
): Promise<TechAuditResult> {
  if (USE_MOCK_DATA) {
    const mock = MOCK_TECH_AUDIT[projectId] ?? {
      pages: [],
      summary: { pages_crawled: null, errors: null, warnings: null },
      source: 'dataforseo.onpage',
      fetched_at: null,
      cache_hit: false,
    };
    return { ...mock, pages: mock.pages.slice(0, limit) };
  }
  const res = await api.get<{
    pages?: TechAuditPageDto[];
    summary?: {
      pages_crawled?: number | string | null;
      errors?: number | string | null;
      warnings?: number | string | null;
    };
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/tech-audit`, { limit });
  return {
    pages: (res.pages ?? []).map((p) => ({
      url: strOrNull(p.url) ?? '—',
      status_code: numOrNull(p.status_code),
      issues: mapIssues(p.issues),
      load_time_ms: numOrNull(p.load_time_ms),
    })),
    summary: {
      pages_crawled: numOrNull(res.summary?.pages_crawled),
      errors: numOrNull(res.summary?.errors),
      warnings: numOrNull(res.summary?.warnings),
    },
    source: res.source ?? 'dataforseo.onpage',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

/* --- 3. Local Map-Rank tracking ---------------------------------------------- */
// GET /seo/projects/{id}/map-rank?keyword=<kw>&location=<key> — PAID
// (local pack), 7d cache. `location` key from the local-locations config.

interface MapRankRowDto {
  keyword?: string | null;
  location?: string | null;
  map_position?: number | string | null;
  business_name?: string | null;
  checked_at?: string | null;
}

const MOCK_MAP_RANK: Record<string, MapRankResult> = {
  seo1: {
    rankings: [
      { keyword: 'community studio cardiff', location: 'uk', map_position: 2, business_name: 'Grow2gether Hub', checked_at: '2026-07-16T06:38:00Z' },
      { keyword: 'community studio cardiff', location: 'uk', map_position: 5, business_name: 'Cardiff Community Space', checked_at: '2026-07-16T06:38:00Z' },
      // null-honest: the business did not appear in the local pack for this
      // check, so map_position is null — never a fabricated 0 or 20.
      { keyword: 'community studio cardiff', location: 'uk', map_position: null, business_name: 'Bay Collective', checked_at: '2026-07-16T06:38:00Z' },
    ],
    source: 'dataforseo.serp.local_pack',
    fetched_at: '2026-07-16T06:38:00Z',
    cache_hit: false,
  },
  seo2: {
    rankings: [
      { keyword: 'therapist near me', location: 'london', map_position: 1, business_name: 'BookSesh Therapy', checked_at: '2026-07-15T18:30:00Z' },
    ],
    source: 'dataforseo.serp.local_pack',
    fetched_at: '2026-07-15T18:30:00Z',
    cache_hit: true,
  },
};

/** Local map-pack rankings for a keyword + location (PAID, 7d cache).
 *  User-triggered only. */
export async function fetchSeoMapRank(
  projectId: string,
  keyword: string,
  location: string,
): Promise<MapRankResult> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_MAP_RANK[projectId] ?? {
        rankings: [],
        source: 'dataforseo.serp.local_pack',
        fetched_at: null,
        cache_hit: false,
      }
    );
  }
  const res = await api.get<{
    rankings?: MapRankRowDto[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/map-rank`, { keyword, location });
  return {
    rankings: (res.rankings ?? []).map((r) => ({
      keyword: strOrNull(r.keyword) ?? keyword,
      location: strOrNull(r.location) ?? location,
      map_position: numOrNull(r.map_position),
      business_name: strOrNull(r.business_name) ?? '—',
      checked_at: strOrEmpty(r.checked_at),
    })),
    source: res.source ?? 'dataforseo.serp.local_pack',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}
